/**
 * DHI Hospitality Dashboard Component
 * Redesigned to match AuditDashboardDesign.html exactly
 * With mobile responsive layout
 */

import { useState } from "react";
import { ScoreRing, Badge, formatCurrency, formatNumber, getGrade, badgeColors, PARAMETER_NAMES, TIER_PARAMETERS, TIER_INFO, PARAMETER_WEIGHTS, calculateTierScore } from "./DashboardShared";
import KeyMetricsTab from "./KeyMetricsTab";
import DiagnosticTab from "./DiagnosticTab";
import RateParityTab from "./RateParityTab";
import ActionPlanTab from "./ActionPlanTab";

const PrintHeader = ({ hotelName }) => (
  <div className="print-header">
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 28px", borderBottom: "1px solid #EEEEEE", position: "relative", background: "#FFFFFF" }}>
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "3px", background: "#1C2470" }} />
      <img src="/dhi-logo.png" style={{ height: "34px", width: "auto" }} alt="dhi Hospitality" />
      <div style={{ textAlign: "right" }}>
        <div style={{ fontSize: "13px", fontWeight: "800", color: "#0A0A1A", letterSpacing: "-0.2px" }}>dhi Hotel Digital Audit</div>
        <div style={{ fontSize: "8.5px", color: "rgba(10,10,26,0.35)", marginTop: "2px" }}>{hotelName} · Confidential</div>
      </div>
    </div>
  </div>
);

const PrintFooter = () => (
  <div className="print-footer">
    <div style={{ background: "#0A0A1A", padding: "12px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
      <div>
        <div style={{ fontSize: "8px", fontWeight: "600", textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(255,255,255,0.35)", marginBottom: "3px" }}>Next Step</div>
        <div style={{ fontSize: "11px", fontWeight: "700", color: "#FFFFFF", marginBottom: "2px" }}>Ready to fix these issues?</div>
        <div style={{ fontSize: "9px", color: "rgba(255,255,255,0.4)" }}>Your personalised action plan prioritises the highest-impact fixes first.</div>
      </div>
      <div style={{ background: "#2D4DE0", borderRadius: "6px", padding: "7px 14px", fontSize: "9px", fontWeight: "600", color: "#FFFFFF", whiteSpace: "nowrap", marginLeft: "16px" }}>
        Book a Free Strategy Call →
      </div>
    </div>
  </div>
);

// =====================================================
// MAIN DASHBOARD COMPONENT
// =====================================================
const Dashboard = ({ data, extractedData, formData, onBack, readOnly = false }) => {
  const [activeTab, setActiveTab] = useState("metrics");
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  // Calendly widget.js auto-initializes elements with class="calendly-inline-widget"
  // No manual initInlineWidgets() call needed - the async script handles it
  
  const handleEmailReport = async () => {
    if (emailSending || emailSent) return;
    setEmailSending(true);
    try {
      const auditId = data?._id || data?.id || data?.auditId;
      const res = await fetch(`/api/audit/${auditId}/email`, { method: "POST" });
      if (res.ok) setEmailSent(true);
      else console.error("Email failed", await res.text());
    } catch (e) { console.error("Email error", e); }
    finally { setEmailSending(false); }
  };

  // Extract data
  const hotelName = extractedData?.hotelName || extractedData?.name || data?.input?.confirmedHotelName || "Hotel";
  const city = extractedData?.city || data?.input?.confirmedCity || "";
  const country = extractedData?.country || data?.input?.confirmedCountry || "";
  const location = [city, country].filter(Boolean).join(", ");
  
  const rooms = data?.input?.rooms || formData?.rooms || 85;
  const adr = data?.input?.adr || formData?.adr || 5000;
  const currency = formData?.currency || data?.input?.currency || data?.paritySnapshot?.currency || data?.parity_snapshot?.currency || "USD";
  
  // Get score from data - prioritize weightedScore
  const score = Math.round(data?.weightedScore || data?.weighted_scoring?.overallWeightedScore || data?.weightedScoring?.overallWeightedScore || data?.overall_score || data?.overallScore || data?.score || 0);
  const scoreGrade = getGrade(score, 100);
  
  const scores = data?.scores || {};
  const financialMetrics = data?.financialMetrics || data?.metrics || {};
  const paritySnapshot = data?.paritySnapshot || data?.parity_snapshot || {};
  const actionPlan = data?.actionPlan || data?.action_plan || [];
  
  // Calculate tier scores using weighted average
  const tierScores = {
    1: calculateTierScore(1, scores),
    2: calculateTierScore(2, scores),
    3: calculateTierScore(3, scores)
  };
  
  // Financial metrics
  const monthlyVisitors = financialMetrics.monthlyVisitors || 1240;
  const benchmarkVisitors = financialMetrics.benchmarkVisitors || 4800;
  const currentCvr = financialMetrics.currentCvrPercent || "0.56%";
  const potentialCvr = "1.50%";
  const totalRecoverable = financialMetrics.totalRecoverableRevenue || data?.metrics?.totalRecoverableRevenue || 16100000;
  
  const tabs = [
    { id: "metrics", label: "Key Metrics" },
    { id: "diagnostic", label: "Digital Diagnostic" },
    { id: "parity", label: "Rate Parity" },
    { id: "action", label: "Action Plan" }
  ];
  
  return (
    <div style={{ background: "#F5F6FA", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" }}>
      <PrintHeader hotelName={hotelName} />
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 16px", background: "#FFFFFF", borderBottom: "1px solid rgba(10,10,26,0.07)" }}>
        <button
          onClick={onBack}
          style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", border: "1px solid rgba(10,10,26,0.12)", borderRadius: 8, background: "#FFFFFF", color: "rgba(10,10,26,0.55)", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
        >
          ← New Audit
        </button>
        {!readOnly && (
          <button
            onClick={() => window.open('https://calendly.com/dhihospitality-audit-review/30min?hide_event_type_details=1&hide_gdpr_banner=1', '_blank')}
            style={{ display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", border: "none", borderRadius: 8, background: "#2D4DE0", color: "#FFFFFF", fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap" }}
          >
            Book Strategy Call →
          </button>
        )}
      </div>
      <div className="dashboard-wrap">
        {/* Header Cards */}
        <div className="header-cards">
          {/* Score Ring Card */}
          <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderRadius: 14, padding: "24px 20px", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minWidth: 180, boxShadow: "0 2px 4px rgba(0,0,0,0.02), 0 8px 24px rgba(0,0,0,0.04)", position: "relative", overflow: "hidden" }}>
            <div style={{ position: "absolute", top: 0, left: 16, right: 16, height: 2, background: "linear-gradient(90deg, #2D4DE0, #4A6FE3, rgba(45,77,224,0))" }} />
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.1em", color: "rgba(10,10,26,0.35)", marginBottom: 10 }}>
              Direct Revenue Score
            </div>
            <ScoreRing score={score} color={badgeColors[scoreGrade.class].background} />
            <div style={{ marginTop: 8, marginBottom: 10 }}>
              <Badge grade={scoreGrade} />
            </div>
            <div style={{ fontSize: 11, color: "rgba(10,10,26,0.4)", textAlign: "center" }}>
              Underperforms vs <span style={{ fontWeight: 700, color: "#0A0A1A" }}>{Math.max(0, 100 - score)}%</span> of industry benchmarks
            </div>
          </div>
          
          {/* Hotel Info Card */}
          <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderRadius: 14, padding: 24, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", boxShadow: "0 2px 4px rgba(0,0,0,0.02), 0 8px 24px rgba(0,0,0,0.04)" }}>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 18, fontWeight: 800, color: "#0A0A1A", marginBottom: 4, lineHeight: 1.3 }}>
              {hotelName}
            </div>
            <div style={{ fontSize: 12, color: "rgba(10,10,26,0.4)", marginBottom: 16 }}>
              {location} · {rooms} rooms · {currency} {formatNumber(adr)} ADR
            </div>
            <div style={{ fontSize: 13, color: "rgba(10,10,26,0.5)", lineHeight: 1.6, marginBottom: 16 }}>
              Your website's direct booking performance has been benchmarked across 10 data points covering search visibility, booking experience, and conversion strength.
            </div>
            <div style={{ fontSize: 13, color: "rgba(10,10,26,0.5)", marginBottom: 12 }}>
              Estimated annual revenue opportunity:
              <span style={{ fontWeight: 700, color: "#0F2A8A", fontSize: 15, marginLeft: 4 }}>{formatCurrency(totalRecoverable, currency)}</span>
            </div>
            <div 
              onClick={() => setActiveTab("action")}
              style={{ fontSize: 13, fontWeight: 600, color: "#2D4DE0", cursor: "pointer" }}
            >
              View prioritised action plan →
            </div>
          </div>
        </div>
        
        {/* Metric Boxes */}
        <div className="metric-boxes">
          <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(10,10,26,0.35)", marginBottom: 4 }}>Potential Visits</div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 800, color: "#0A0A1A", marginBottom: 2 }}>{formatNumber(benchmarkVisitors)}</div>
            <div style={{ fontSize: 10, color: "rgba(10,10,26,0.3)" }}>vs current {formatNumber(monthlyVisitors)}/mo</div>
          </div>
          <div style={{ background: "#FFFFFF", border: "1px solid rgba(10,10,26,0.08)", borderRadius: 14, padding: 14 }}>
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(10,10,26,0.35)", marginBottom: 4 }}>Potential Conversion</div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 800, color: "#0A0A1A", marginBottom: 2 }}>{potentialCvr}</div>
            <div style={{ fontSize: 10, color: "rgba(10,10,26,0.3)" }}>vs current {currentCvr}</div>
          </div>
          <div style={{ background: "#0F2A8A", borderRadius: 10, padding: 14 }}>
            <div style={{ fontSize: 9, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.08em", color: "rgba(255,255,255,0.5)", marginBottom: 4 }}>Revenue Opportunity</div>
            <div style={{ fontFamily: "'Outfit', sans-serif", fontSize: 17, fontWeight: 800, color: "#FFFFFF", marginBottom: 2 }}>{formatCurrency(totalRecoverable, currency)}</div>
            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.4)" }}>Annual estimate</div>
          </div>
        </div>
        
        {/* Disclaimer */}
        <div style={{ fontSize: 10, fontWeight: 600, color: "rgba(10,10,26,0.35)", textAlign: "center", padding: "6px 12px", marginBottom: 8, lineHeight: 1.5 }}>
          <span style={{ fontWeight: 700, color: "rgba(10,10,26,0.45)" }}>Disclaimer:</span> Projections based on your property inputs and publicly available data including rate parity, search rankings, and page performance at the time of audit. All estimates are indicative and do not constitute a guarantee of performance.
        </div>
        
        {/* Tab Bar with opaque background */}
        <div className="tab-bar-wrapper">
          <div className="tab-bar">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                data-testid={`tab-${tab.id}`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Tab Content */}
        {activeTab === "metrics" && (
          <KeyMetricsTab scores={scores} tierScores={tierScores} onTabChange={setActiveTab} />
        )}
        {activeTab === "diagnostic" && (
          <DiagnosticTab data={data} tierScores={tierScores} onTabChange={setActiveTab} />
        )}
        {activeTab === "parity" && (
          <RateParityTab paritySnapshot={paritySnapshot} currency={currency} onTabChange={setActiveTab} />
        )}
        {activeTab === "action" && (
          <ActionPlanTab actionPlan={actionPlan} data={data} />
        )}
      </div>
      <PrintFooter />
    </div>
  );
};

export default Dashboard;
